/* story-timeline.js — Story timeline axe rider (rebrand 2026, W3 lane 3.3 BR7).

   Port of the FROZEN prototype behavioural contract (prototype-c/js/story.js):
   the axe icon rides the serpentine trail via getPointAtLength, spinning
   540° across the section's scroll progress, while the red "lit" copy of
   the trail is revealed behind it through the mask path's stroke-dashoffset.
   Contract constants carried verbatim on desktop: 540° spin, lerp 0.14,
   settle epsilon 0.0004, progress anchored to the point crossing 55% of
   viewport height. Mobile uses a restrained 0.10 lerp so touch-scroll jumps
   settle less abruptly without changing the path progress or final position.
   IntersectionObserver-gated rAF loop (rootMargin 120px — no rAF while
   off-screen), passive scroll listener, re-measure on resize AND load
   (fonts/images shift geometry).

   Deviations from the prototype IIFE (all §14 editor-lifecycle wrapping):
   - <story-timeline> custom element, guarded define; ALL state lives on the
     instance (two instances on one page animate independently — though the
     story-trail snippet's fixed mask id keeps the SECTION one-per-page).
   - Geometry is read from the element's own subtree only.
   - The axe rider is OPTIONAL (schema `show_axe`): without it the lit trail
     still animates — the prototype bailed when the rider was missing.
   - Listeners bind with an AbortController signal; one abort() plus
     IntersectionObserver disconnect + cancelAnimationFrame in
     disconnectedCallback.
   - Theme editor (design mode only): shopify:section:select re-measures and
     SNAPS to the live progress (scroll/time-based behaviour honoured,
     CONTRACTS §14.3); shopify:block:select scrolls the selected milestone
     (or deco sticker) into view.

   Reduced motion (CONTRACTS §1.4): connectedCallback binds only the design-
   mode editor listeners (shopify:block:select must still scroll for reduce-
   motion merchants), then exits before touching the DOM — the axe stays
   hidden (CSS display:none + never .is-active) and the never-dashed mask
   leaves the red trail fully drawn, both pure CSS/SVG states. Matches the
   prototype's init-time matchMedia check; onSectionSelect self-guards so it
   never dashes the trail on this path. */

if (!customElements.get('story-timeline')) {
  customElements.define(
    'story-timeline',
    class StoryTimeline extends HTMLElement {
      static SPIN_DEGREES = 540;
      static LERP = 0.14;
      static MOBILE_LERP = 0.1;
      static SETTLE_EPSILON = 0.0004;
      static VIEWPORT_ANCHOR = 0.55;

      connectedCallback() {
        this.svg = this.querySelector('.trail');
        this.basePath = this.querySelector('.trail-base');
        this.maskPath = this.querySelector('.trail-mask-path');
        this.rider = this.querySelector('.axe-rider');
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.mobileViewport = window.matchMedia('(max-width: 720px)');

        /* One teardown handle for BOTH init paths — disconnectedCallback
           aborts it whether or not the animation state below initialises. */
        this.listenersController = new AbortController();
        const { signal } = this.listenersController;

        /* Editor listeners bind BEFORE the reduced-motion exit so
           shopify:block:select still scrolls the selected block into view
           for reduce-motion merchants (§14.3). All animation state stays
           behind the guard below; onSectionSelect self-guards. */
        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:section:select', (event) => this.onSectionSelect(event), { signal });
          document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
        }

        /* Den clip (CONTRACTS §1.4). The markup autoplays with zero JS, which
           is the right default and keeps the no-JS path whole; the only thing
           that needs code is the reduce path, because a 27s clip looping
           forever is precisely the moving content this query exists to stop.
           There we pause it and hand over real controls, so the merchant can
           still watch it deliberately. Runs BEFORE the trail's reduce exit and
           self-guards on the querySelector, so it is a no-op both on reduce-
           motion trails and on milestones that ship no video. */
        const denVideo = this.querySelector('.den-video__player');
        if (denVideo && reduceMotion.matches) {
          denVideo.autoplay = false;
          denVideo.loop = false;
          denVideo.controls = true;
          denVideo.pause();
        }

        if (!this.svg || !this.basePath || !this.maskPath || reduceMotion.matches) return;

        this.pathLength = this.basePath.getTotalLength();
        this.viewBox = this.svg.viewBox.baseVal;

        /* Until the first update, keep the lit trail unrevealed. */
        this.maskPath.style.strokeDasharray = String(this.pathLength);
        this.maskPath.style.strokeDashoffset = String(this.pathLength);

        /* Fresh baseline on every (re)connect — editor re-injection safe. */
        this.boxTop = 0;
        this.boxHeight = 1;
        this.boxWidth = 1;
        this.current = 0;
        this.target = 0;
        this.running = false;
        this.rafId = 0;
        this.boundTick = () => this.tick();

        window.addEventListener('scroll', () => this.readTarget(), { passive: true, signal });
        window.addEventListener('resize', () => this.measure(), { signal });
        window.addEventListener('load', () => this.measure(), { signal });

        this.gate = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.measure();
                this.start();
              } else {
                this.stop();
              }
            });
          },
          { rootMargin: '120px 0px' }
        );
        this.gate.observe(this);

        /* First paint: place the axe at the correct spot immediately. */
        this.measure();
        this.current = this.target;
        this.render(this.current);
      }

      disconnectedCallback() {
        if (this.listenersController) {
          this.listenersController.abort();
          this.listenersController = null;
        }
        if (this.gate) {
          this.gate.disconnect();
          this.gate = null;
        }
        this.running = false;
        if (this.rafId) {
          window.cancelAnimationFrame(this.rafId);
          this.rafId = 0;
        }
      }

      measure() {
        const rect = this.getBoundingClientRect();
        const trailRect = this.svg.getBoundingClientRect();
        this.boxTop = rect.top + window.scrollY;
        this.boxHeight = Math.max(rect.height, 1);
        this.pathLeft = trailRect.left - rect.left;
        this.pathWidth = Math.max(trailRect.width, 1);
        this.readTarget();
      }

      readTarget() {
        const anchor = window.scrollY + window.innerHeight * StoryTimeline.VIEWPORT_ANCHOR - this.boxTop;
        this.target = Math.min(1, Math.max(0, anchor / this.boxHeight));
      }

      render(progress) {
        const point = this.basePath.getPointAtLength(progress * this.pathLength);
        const x = this.pathLeft + (point.x / this.viewBox.width) * this.pathWidth;
        const y = (point.y / this.viewBox.height) * this.boxHeight;

        if (this.rider) {
          /* Contract transform (prototype js/story.js:61-63): rider position +
             spin, NOT a §1.3 tilt — the tilt rule governs static decorative
             rotation in CSS; this is the scroll-driven animation itself. */
          this.rider.style.transform =
            'translate(' + x + 'px, ' + y + 'px) translate(-50%, -50%) rotate(' +
            progress * StoryTimeline.SPIN_DEGREES + 'deg)';
        }
        this.maskPath.style.strokeDashoffset = String(this.pathLength * (1 - progress));
      }

      tick() {
        const lerp = this.mobileViewport.matches ? StoryTimeline.MOBILE_LERP : StoryTimeline.LERP;
        this.current += (this.target - this.current) * lerp;

        if (Math.abs(this.target - this.current) < StoryTimeline.SETTLE_EPSILON) {
          this.current = this.target;
        }

        this.render(this.current);
        this.rafId = this.running ? window.requestAnimationFrame(this.boundTick) : 0;
      }

      start() {
        if (this.running) return;
        this.running = true;
        if (this.rider) this.rider.classList.add('is-active');
        this.rafId = window.requestAnimationFrame(this.boundTick);
      }

      stop() {
        this.running = false;
        if (this.rafId) {
          window.cancelAnimationFrame(this.rafId);
          this.rafId = 0;
        }
      }

      onSectionSelect(event) {
        if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
        /* Bound on both init paths — bail before the animation state exists
           (reduced motion, or a missing trail): measuring is harmless but
           snapping/rendering would dash the pure-CSS fully-drawn trail. */
        if (this.pathLength === undefined) return;
        /* Scroll/time-based behaviour honoured (§14.3): re-measure against the
           editor-adjusted layout and snap straight to the live progress so the
           axe/trail never play a catch-up glitch on select. */
        this.measure();
        this.current = this.target;
        this.render(this.current);
      }

      onBlockSelect(event) {
        if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
        if (!(event.target instanceof Element)) return;
        const blockEl = event.target.closest('.milestone, .trail-deco');
        if (blockEl && this.contains(blockEl)) {
          blockEl.scrollIntoView({ block: 'center' });
        }
      }
    }
  );
}
